/*
 * Client side of GET /api/leaves/{leafId}/history (concept C.9,
 * docs/history-aggregation.md).
 *
 * The endpoint answers in one of two modes:
 *
 * - shape: the M4 polyline of all raw points, one column per pixel. Drawn as a
 *   step line (ECharts `step: 'end'`) it is pixel-identical to the raw data;
 *   `v = null` breaks the line where no value was valid. FOR DISPLAY ONLY —
 *   means, kWh, differences, KPIs or exports computed from it are wrong
 *   because the time weighting is missing.
 * - exact: one value per interval, computed on the server from the raw
 *   points (time-weighted step function, counters linear between readings).
 *   This is the path for values, KPIs and export.
 *
 * Pure module without DOM access so it can be tested with `node --test`.
 */

/** Data point kinds as `LeafDto.metricKind` names them. */
export type MetricKindName =
  | 'Unknown'
  | 'Instantaneous'
  | 'CumulativeCounter'
  | 'Rate'
  | 'State'
  | 'Setpoint';

/**
 * Aggregations the server offers (AggregationFunction.cs minus the reserved
 * Sum 1, StdDev 8 and MovingAverage 12 — the moving average was removed on
 * 22.10.2026; smoothing is a display option of the chart).
 */
export type AggregationName =
  | 'Mean'
  | 'Min'
  | 'Max'
  | 'Count'
  | 'First'
  | 'Last'
  | 'Range'
  | 'Integral'
  | 'Difference'
  | 'Rate';

/** Numeric wire values of AggregationFunction.cs. Must never change. */
export const AGGREGATION_IDS: Readonly<Record<AggregationName, number>> = {
  Mean: 0,
  Min: 2,
  Max: 3,
  Count: 4,
  First: 5,
  Last: 6,
  Range: 7,
  Integral: 9,
  Difference: 10,
  Rate: 11,
};

/** One point on the wire. `c` (coverage 0…1) and `f` (marks) only in exact mode. */
export interface HistoryPointWire {
  t: string;
  v: number | null;
  q: string;
  c?: number;
  f?: string[];
}

/** Mirrors GetLeafHistoryResponse.cs. */
export interface LeafHistoryResponse {
  leafId: string;
  from: string;
  to: string;
  /** Interval (exact) or column width (shape) as .NET TimeSpan string. */
  interval: string;
  /** Applied aggregation (numeric); null in shape mode. */
  aggregation: number | null;
  metricKind: number;
  points: HistoryPointWire[];
  method: 'exact' | 'shape';
  /** Derived unit of the values, e.g. kWh for the integral over kW. */
  unit: string | null;
  holdSeconds: number;
  /** `assumed`: the data point has no heartbeat, the hold time is a guess. */
  holdSource: 'heartbeat' | 'assumed';
  allowedAggregations: number[];
}

// --------------------------------------------------------------- catalog

const MOMENTARY: readonly AggregationName[] = ['Mean', 'Min', 'Max', 'First', 'Last', 'Range', 'Count'];
const FLOW: readonly AggregationName[] = [...MOMENTARY, 'Integral'];
const COUNTER: readonly AggregationName[] = ['Difference', 'Rate', 'First', 'Last', 'Count'];
const STATE_MULTI: readonly AggregationName[] = ['First', 'Last', 'Count'];
const STATE_BINARY: readonly AggregationName[] = ['First', 'Last', 'Count', 'Mean'];
const UNCLASSIFIED: readonly AggregationName[] = ['First', 'Last', 'Min', 'Max'];

/**
 * Aggregations allowed for a data point, in display order. Same table as
 * AggregationCatalog.cs; both are checked against
 * docs/testvectors/aggregation-catalog.json.
 */
export function allowedAggregations(metricKind: string, valueType: string): readonly AggregationName[] {
  switch (metricKind) {
    case 'Instantaneous':
    case 'Setpoint':
      return MOMENTARY;
    case 'Rate':
      return FLOW;
    case 'CumulativeCounter':
      return COUNTER;
    case 'State':
      return isBinary(valueType) ? STATE_BINARY : STATE_MULTI;
    default:
      return UNCLASSIFIED;
  }
}

/** Aggregation the server applies when the request names none. */
export function defaultAggregation(metricKind: string): AggregationName {
  switch (metricKind) {
    case 'Instantaneous':
    case 'Setpoint':
    case 'Rate':
      return 'Mean';
    case 'CumulativeCounter':
      return 'Difference';
    default:
      return 'Last';
  }
}

function isBinary(valueType: string): boolean {
  return valueType === 'Bool';
}

/**
 * What a series can show in the chart in mode "Berechnet", in display order.
 * A subset of the catalog: the diagnostic values (count of reports, range,
 * first and last value) belong in the key figures of the window, not on a
 * chart axis — except "last value", which is the meaningful value of a state.
 */
export function chartCalculations(metricKind: string, valueType: string): readonly AggregationName[] {
  switch (metricKind) {
    case 'Instantaneous':
    case 'Setpoint':
      return ['Mean', 'Min', 'Max'];
    case 'Rate':
      return ['Mean', 'Integral', 'Min', 'Max'];
    case 'CumulativeCounter':
      return ['Difference', 'Rate'];
    case 'State':
      return isBinary(valueType) ? ['Mean', 'Last'] : ['Last'];
    default:
      return ['Last', 'Min', 'Max'];
  }
}

/** Calculation a series starts with in mode "Berechnet"; the catalog default. */
export function defaultChartCalculation(metricKind: string, valueType: string): AggregationName {
  const d = defaultAggregation(metricKind);
  return chartCalculations(metricKind, valueType).includes(d) ? d : chartCalculations(metricKind, valueType)[0];
}

// ---------------------------------------------------------------- labels

const LABELS: Readonly<Record<AggregationName, string>> = {
  Mean: 'Mittelwert',
  Min: 'Minimum',
  Max: 'Maximum',
  Count: 'Anzahl Meldungen',
  First: 'Erster Wert',
  Last: 'Letzter Wert',
  Range: 'Spannweite',
  Integral: 'Menge',
  Difference: 'Verbrauch',
  Rate: 'Zuwachs pro Stunde',
};

/** UI label (German). The mean of a Bool state is its duty cycle. */
export function aggregationLabel(name: AggregationName, metricKind?: string, valueType?: string): string {
  if (name === 'Mean' && metricKind === 'State' && valueType !== undefined && isBinary(valueType)) {
    return 'Einschaltanteil';
  }
  return LABELS[name];
}

/** Label for the shape mode. */
export const SHAPE_LABEL = 'Rohdaten';

/** Label for the computed mode. */
export const COMPUTED_LABEL = 'Berechnet';

/**
 * Whether the value of an aggregation is an amount per interval (and so
 * depends on the interval length): quantity and consumption. A mean, min or
 * max of 5 min and of 1 h have the same unit and scale; 9 l per 5 min and
 * 108 l per hour do not.
 */
export function isPerInterval(name: AggregationName): boolean {
  return name === 'Integral' || name === 'Difference';
}

/** Label of a computed series, e.g. `Mittelwert je 5 min`, `Einschaltanteil je 1 h`. */
export function calculationLabel(name: AggregationName, metricKind: string, valueType: string, intervalMs: number): string {
  return `${aggregationLabel(name, metricKind, valueType)} je ${formatIntervalMs(intervalMs)}`;
}

const SHORT_LABELS: Readonly<Record<AggregationName, string>> = {
  Mean: 'Ø',
  Min: 'Min',
  Max: 'Max',
  Count: 'Anzahl',
  First: 'erster Wert',
  Last: 'letzter Wert',
  Range: 'Spannweite',
  Integral: 'Menge',
  Difference: 'Verbrauch',
  Rate: 'Zuwachs/h',
};

/**
 * Short legend label of a computed series: `Vorlauf (Ø je 5 min)`,
 * `Wärmemenge (Verbrauch je 15 min)`, `Verdichter (Ein-Anteil je 1 h)`.
 * "je" so that `letzter Wert 5 min` is not read as "the last 5 minutes". The long
 * form (calculationLabel) made the legend scroll with three series.
 */
export function legendLabel(
  seriesName: string,
  name: AggregationName,
  metricKind: string,
  valueType: string,
  intervalMs: number,
): string {
  const dutyCycle = name === 'Mean' && metricKind === 'State' && isBinary(valueType);
  return `${seriesName} (${dutyCycle ? 'Ein-Anteil' : SHORT_LABELS[name]} je ${formatIntervalMs(intervalMs)})`;
}

/**
 * Axis unit of a computed series. Amounts per interval carry the interval,
 * e.g. `l je 5 min`, so the scale is not mistaken for a rate.
 */
export function calculationUnit(name: AggregationName, unit: string | null, intervalMs: number): string | null {
  if (!isPerInterval(name) || !unit) return unit;
  return `${unit} je ${formatIntervalMs(intervalMs)}`;
}

const MARK_LABELS: Readonly<Record<string, string>> = {
  counterDiscontinuity: 'Zählersprung, mit 0 angesetzt',
  counterGap: 'Ablesung länger als die Haltedauer ausgeblieben',
  counterWrap: 'Zählerüberlauf',
};

/** UI label (German) for a mark of an exact interval value. */
export function markLabel(mark: string): string {
  return MARK_LABELS[mark] ?? mark;
}

export function aggregationNameFromId(id: number | null | undefined): AggregationName | null {
  if (id === null || id === undefined) return null;
  for (const [name, value] of Object.entries(AGGREGATION_IDS) as [AggregationName, number][]) {
    if (value === id) return name;
  }
  return null;
}

/**
 * Parses a stored or suggested aggregation key. Legacy keys from before the
 * catalog (`auto`, `sum`, anything unknown) become the shape: drawing the
 * raw data is always correct, a guessed aggregation is not.
 */
export function parseAggregationKey(key: string | null | undefined): 'shape' | AggregationName {
  if (!key) return 'shape';
  const normalised = key.replace(/[-_\s]/g, '').toLowerCase();
  // Removed 22.10.2026; a stored moving average becomes the mean.
  if (normalised === 'movingaverage') return 'Mean';
  for (const name of Object.keys(AGGREGATION_IDS) as AggregationName[]) {
    if (name.toLowerCase() === normalised) return name;
  }
  return 'shape';
}

// --------------------------------------------------------------- request

/** Shape: one M4 column per pixel. Exact: interval values; server default without `aggregation`. */
export type HistoryRequest =
  | { mode: 'shape'; widthPx: number }
  | { mode: 'exact'; aggregation?: AggregationName; interval?: string };

/** Server limits of the `width` parameter. */
export const MIN_WIDTH_PX = 10;
export const MAX_WIDTH_PX = 8000;

export function historyPath(leafId: string, from: Date, to: Date, request: HistoryRequest): string {
  const params = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
  if (request.mode === 'shape') {
    const width = Math.round(Math.min(MAX_WIDTH_PX, Math.max(MIN_WIDTH_PX, request.widthPx)));
    params.set('aggregation', 'shape');
    params.set('width', String(width));
  } else {
    if (request.aggregation) params.set('aggregation', request.aggregation);
    if (request.interval) params.set('interval', request.interval);
  }
  return `/api/leaves/${leafId}/history?${params.toString()}`;
}

// ---------------------------------------------------------------- series

/** [epoch ms, value]; `null` breaks the line. */
export type SeriesPoint = [number, number | null];

/**
 * Chart series for a response, to be drawn as a step line (`step: 'end'`):
 * every point holds its value until the next one.
 *
 * Shape points already carry the gaps. Exact interval values get an explicit
 * end point at the end of their interval, and a `null` where the next
 * interval has no value, so a missing interval shows as a gap instead of a
 * line bridging it.
 */
export function toSeries(response: LeafHistoryResponse): SeriesPoint[] {
  if (response.method === 'shape') {
    return response.points.map((p) => [Date.parse(p.t), finiteOrNull(p.v)]);
  }

  const intervalMs = parseTimeSpanMs(response.interval);
  const toMs = Date.parse(response.to);
  if (intervalMs === null || !Number.isFinite(toMs)) {
    return response.points.map((p) => [Date.parse(p.t), finiteOrNull(p.v)]);
  }

  return exactToSteps(response.points, intervalMs, toMs);
}

/**
 * Finer points for the zoomed part of the chart, merged into the points of
 * the whole window: the base before and after the zoom window, the detail
 * inside. Points are steps (each holds until the next), so the detail starts
 * with its last point at or before the zoom start and the base resumes after
 * the zoom end. Without detail points inside the window the base stays.
 */
export function mergeZoomDetail(base: SeriesPoint[], detail: SeriesPoint[], window: [number, number]): SeriesPoint[] {
  const [from, to] = window;
  let start = 0;
  for (let i = 0; i < detail.length && detail[i][0] <= from; i++) start = i;
  let end = start;
  while (end < detail.length && detail[end][0] <= to) end++;
  const inside = detail.slice(start, end);
  if (inside.length === 0) return base;
  const cut = Math.min(inside[0][0], from);
  return [
    ...base.filter((p) => p[0] < cut),
    ...inside,
    ...base.filter((p) => p[0] > to),
  ];
}

/**
 * Points for a smoothed line — a display option only, never for values.
 *
 * A spline through the step points would loop: the shape has up to four
 * points per column at the same timestamp (first, min, max, last), and step
 * series have an end point and the next start at the same timestamp. So each
 * run of points with the same timestamp is reduced to its last value; a gap
 * (`null`) in the run is kept after it. Short spikes within one pixel column
 * disappear — that is the price of the smooth look.
 */
export function smoothForDisplay(points: SeriesPoint[]): SeriesPoint[] {
  const out: SeriesPoint[] = [];
  let i = 0;
  while (i < points.length) {
    const t = points[i][0];
    let last: number | null = null;
    let gap = false;
    for (; i < points.length && points[i][0] === t; i++) {
      const v = points[i][1];
      if (v === null) gap = true;
      else { last = v; gap = false; }
    }
    if (last !== null) out.push([t, last]);
    if (gap) out.push([t, null]);
  }
  return out;
}

/**
 * SVG path of a step line through `points`: horizontal at the previous value
 * up to the next point, then vertical. `null` lifts the pen.
 */
export function stepPath(
  points: SeriesPoint[],
  xFor: (ms: number) => number,
  yFor: (value: number) => number,
): string {
  const parts: string[] = [];
  let lastY: number | null = null;
  for (const [t, v] of points) {
    if (v === null) {
      lastY = null;
      continue;
    }
    const x = xFor(t).toFixed(1);
    const y = yFor(v);
    if (lastY === null) parts.push(`M${x},${y.toFixed(1)}`);
    else parts.push(`L${x},${lastY.toFixed(1)}`, `L${x},${y.toFixed(1)}`);
    lastY = y;
  }
  return parts.join(' ');
}

/** See {@link toSeries}. Exported for tests. */
export function exactToSteps(points: HistoryPointWire[], intervalMs: number, toMs: number): SeriesPoint[] {
  const out: SeriesPoint[] = [];
  for (let i = 0; i < points.length; i++) {
    const start = Date.parse(points[i].t);
    const value = finiteOrNull(points[i].v);
    const end = Math.min(alignedEnd(start, intervalMs), toMs);
    out.push([start, value]);
    const next = i + 1 < points.length ? Date.parse(points[i + 1].t) : null;
    if (next === null) {
      out.push([end, value]);
    } else if (next > end) {
      out.push([end, value], [end, null]);
    }
  }
  return out;
}

/**
 * End of the interval that starts at `startMs`. Intervals are aligned to the
 * Unix epoch (IntervalGrid.cs); only the first one may start late because it
 * is clipped to the window.
 */
function alignedEnd(startMs: number, intervalMs: number): number {
  return Math.floor(startMs / intervalMs) * intervalMs + intervalMs;
}

function finiteOrNull(v: number | null | undefined): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** Parses a .NET TimeSpan string (`hh:mm:ss`, `d.hh:mm:ss`, optional fraction). */
export function parseTimeSpanMs(value: string): number | null {
  const m = /^(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d+))?$/.exec(value);
  if (!m) return null;
  const days = Number(m[1] ?? 0);
  const seconds = ((days * 24 + Number(m[2])) * 60 + Number(m[3])) * 60 + Number(m[4]);
  const fraction = m[5] ? Number(`0.${m[5]}`) : 0;
  return Math.round((seconds + fraction) * 1000);
}

/**
 * Interval levels of the server (HistoryComposer.IntervalLevels) and the
 * target count it picks the level for. Same rule as on the server, so the UI
 * can name the raster before the data arrive.
 */
export const INTERVAL_LEVELS_S: readonly number[] = [
  1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 10_800, 21_600, 43_200, 86_400, 172_800, 604_800,
];
export const TARGET_INTERVALS = 400;

/** Interval the server picks for a window when the request names none. */
export function autoIntervalMs(windowMs: number): number {
  const wanted = windowMs / TARGET_INTERVALS / 1000;
  for (const s of INTERVAL_LEVELS_S) if (s >= wanted) return s * 1000;
  return INTERVAL_LEVELS_S[INTERVAL_LEVELS_S.length - 1] * 1000;
}

/** `interval` query value (.NET TimeSpan, `d.hh:mm:ss`). */
export function timeSpanParam(ms: number): string {
  let s = Math.max(1, Math.round(ms / 1000));
  const d = Math.floor(s / 86_400);
  s -= d * 86_400;
  const pad = (n: number) => n.toString().padStart(2, '0');
  const hms = `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
  return d > 0 ? `${d}.${hms}` : hms;
}

/** Short German label of an interval length, e.g. `5 min`, `1 h`, `1 Tag`. */
export function formatIntervalMs(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s >= 86_400 && s % 86_400 === 0) return s === 86_400 ? '1 Tag' : `${s / 86_400} Tage`;
  if (s >= 3600 && s % 3600 === 0) return `${s / 3600} h`;
  if (s >= 60 && s % 60 === 0) return `${s / 60} min`;
  return `${s} s`;
}

// ------------------------------------------------------------------- CSV

/** One data point in an exact export. */
export interface ExactExportColumn {
  name: string;
  /** Applied aggregation, for the header. */
  aggregation: AggregationName;
  metricKind: string;
  valueType: string;
  response: LeafHistoryResponse;
}

/**
 * CSV of exact interval values: one row per interval start (UTC), per data
 * point the value, its coverage (share of the interval with a valid value)
 * and its marks. Only exact responses belong here — never shape points.
 */
export function exactCsv(columns: ExactExportColumn[]): string {
  const header = ['Intervallbeginn (UTC)'];
  for (const col of columns) {
    const unit = col.response.unit ? `, ${col.response.unit}` : '';
    header.push(
      `${col.name} [${aggregationLabel(col.aggregation, col.metricKind, col.valueType)}${unit}]`,
      `${col.name} Abdeckung`,
      `${col.name} Hinweise`,
    );
  }

  const byTime = new Map<number, (string | number)[]>();
  columns.forEach((col, index) => {
    if (col.response.method !== 'exact') {
      throw new Error('Only exact responses can be exported.');
    }
    for (const p of col.response.points) {
      const t = Date.parse(p.t);
      let row = byTime.get(t);
      if (!row) {
        row = new Array<string | number>(columns.length * 3).fill('');
        byTime.set(t, row);
      }
      row[index * 3] = finiteOrNull(p.v) ?? '';
      row[index * 3 + 1] = p.c ?? '';
      row[index * 3 + 2] = (p.f ?? []).map(markLabel).join('; ');
    }
  });

  const lines = [header.map(csvCell).join(',')];
  for (const t of [...byTime.keys()].sort((a, b) => a - b)) {
    lines.push([new Date(t).toISOString(), ...byTime.get(t)!].map(csvCell).join(','));
  }
  return lines.join('\r\n');
}

function csvCell(value: string | number): string {
  const s = String(value);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// --------------------------------------------------------------- summary

/** Mirrors GetLeafSummaryResponse.cs: key figures over the whole window. */
export interface LeafSummaryResponse {
  leafId: string;
  from: string;
  to: string;
  metricKind: number | string;
  unit: string | null;
  holdSeconds: number;
  holdSource: 'heartbeat' | 'assumed';
  /** Share 0…1 of the window with a valid value. */
  coverage: number;
  count: number;
  /** Reports in the window discarded as invalid (marked at ingest or outside
   *  the validity range). Missing from servers before 22.10.2026. */
  invalidCount?: number;
  mean?: number;
  dutyCycle?: number;
  min?: number;
  max?: number;
  range?: number;
  first?: number;
  last?: number;
  quantity?: number;
  quantityUnit?: string;
  consumption?: number;
  flags?: string[];
  /** Last report before the window end, also before the window. */
  lastReportAt?: string;
  lastValue?: number;
}

export function summaryPath(leafId: string, from: Date, to: Date): string {
  const params = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
  return `/api/leaves/${leafId}/summary?${params.toString()}`;
}

/** One data point of a key-figure export. */
export interface SummaryExportRow {
  name: string;
  symbol: string;
  summary: LeafSummaryResponse;
}

const SUMMARY_HEADER = [
  'Datenpunkt', 'Symbol', 'Von (UTC)', 'Bis (UTC)', 'Einheit',
  'Mittelwert', 'Einschaltanteil', 'Min', 'Max', 'Spannweite', 'Erster', 'Letzter',
  'Menge', 'Mengeneinheit', 'Verbrauch',
  'Meldungen', 'Ungültig verworfen', 'Abdeckung', 'Hinweise', 'Letzte Meldung (UTC)',
] as const;

/**
 * Key figures as a table: header plus one row per data point. Numbers stay
 * numbers (no unit in the cell), so a spreadsheet can compute with them;
 * empty where the figure does not apply.
 */
export function summaryTable(rows: SummaryExportRow[]): (string | number)[][] {
  const out: (string | number)[][] = [[...SUMMARY_HEADER]];
  for (const { name, symbol, summary: s } of rows) {
    const n = (v: number | undefined): number | string => (v !== undefined && Number.isFinite(v) ? v : '');
    out.push([
      name,
      symbol,
      s.from,
      s.to,
      s.unit ?? '',
      n(s.mean),
      n(s.dutyCycle),
      n(s.min),
      n(s.max),
      n(s.range),
      n(s.first),
      n(s.last),
      n(s.quantity),
      s.quantityUnit ?? '',
      n(s.consumption),
      s.count,
      n(s.invalidCount),
      s.coverage,
      (s.flags ?? []).map(markLabel).join('; '),
      s.lastReportAt ?? '',
    ]);
  }
  return out;
}

/** Key figures as CSV, in the same format as the interval export (comma, decimal point, UTC). */
export function summaryCsv(rows: SummaryExportRow[]): string {
  return summaryTable(rows).map((r) => r.map(csvCell).join(',')).join('\r\n');
}

const TSV_NUMBER = new Intl.NumberFormat('de-AT', { maximumFractionDigits: 6, useGrouping: false });

/**
 * Key figures as tab-separated text for the clipboard: pastes as a table into
 * Excel or a document. Numbers with decimal comma, as a German-language
 * spreadsheet expects.
 */
export function summaryTsv(rows: SummaryExportRow[]): string {
  return summaryTable(rows)
    .map((r) => r.map((v) => (typeof v === 'number' ? TSV_NUMBER.format(v) : v.replace(/[\t\r\n]+/g, ' '))).join('\t'))
    .join('\n');
}
